// src/pages/Login.tsx
import { useState, KeyboardEvent, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Fade,
  InputAdornment,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
  Snackbar,
  FormHelperText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { login } from "../Services/authService";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SettingsIcon from "@mui/icons-material/Settings";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import LockIcon from "@mui/icons-material/Lock";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Styled components with enhanced design
const LoginContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.background.default})`,
  backgroundSize: "cover",
  overflow: "hidden",
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 5),
  width: "100%",
  maxWidth: "420px",
  borderRadius: "16px",
  boxShadow: "0 12px 28px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "4px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 5),
  width: "100%",
  maxWidth: "520px",
  borderRadius: "16px",
  boxShadow: "0 12px 28px rgba(0, 0, 0, 0.1)",
  marginRight: theme.spacing(6),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    right: 0,
    width: "140px",
    height: "140px",
    background: `radial-gradient(circle, ${theme.palette.primary.main}10, transparent 70%)`,
    borderRadius: "50%",
    zIndex: 0,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: "8px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 6px 16px ${theme.palette.primary.main}30`,
  },
}));

const InputField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: "0 3px 8px rgba(0, 0, 0, 0.06)",
    },
    "&.Mui-focused": {
      boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    transition: "all 0.2s ease",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.primary.main,
  },
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(3),
  position: "relative",
  zIndex: 1,
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: "white",
  width: 56,
  height: 56,
  borderRadius: "14px",
  marginRight: theme.spacing(2.5),
  boxShadow: `0 6px 14px ${theme.palette.primary.main}30`,
}));

const CentralIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: "white",
  width: 80,
  height: 80,
  borderRadius: "20px",
  margin: "0 auto",
  marginBottom: theme.spacing(4),
  boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
}));

const MotionBox = styled(motion.div)({
  width: "100%",
});

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100 }
  }
};

interface FormStateType {
  email: {
    value: string;
    error: string;
  };
  password: {
    value: string;
    error: string;
  };
}

const Login = () => {
  const [formState, setFormState] = useState<FormStateType>({
    email: { value: "", error: "" },
    password: { value: "", error: "" },
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));

  // Form validation
  const validateForm = (): boolean => {
    const newFormState = { ...formState };
    let isValid = true;

    if (!formState.email.value) {
      newFormState.email.error = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formState.email.value)) {
      newFormState.email.error = "Please enter a valid email address";
      isValid = false;
    } else {
      newFormState.email.error = "";
    }

    if (!formState.password.value) {
      newFormState.password.error = "Password is required";
      isValid = false;
    } else if (formState.password.value.length < 6) {
      newFormState.password.error = "Password must be at least 6 characters";
      isValid = false;
    } else {
      newFormState.password.error = "";
    }

    setFormState(newFormState);
    return isValid;
  };

  const handleInputChange = (field: 'email' | 'password', value: string) => {
    setFormState({
      ...formState,
      [field]: { value, error: "" }
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await login(formState.email.value, formState.password.value);
      
      if (!response || !response.id) {
        throw new Error("Login failed");
      }

      setShowSuccessMessage(true);
      
      setTimeout(() => {
        const role = response.role?.name.toLowerCase();
        
        if (role === "admin") {
          navigate("/dashboard");
        } else if (role === "hr") {
          navigate("/hr-dashboard");
        } else {
          navigate("/unauthorized");
        }
      }, 1500);
      
    } catch (err) {
      setFormState({
        ...formState,
        password: { ...formState.password, error: "Invalid email or password" }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <LoginContainer maxWidth={false} disableGutters>
      <Fade in={true} timeout={800}>
        <Grid container justifyContent="center" spacing={0} sx={{ maxWidth: '1200px' }}>
          {!isMobile && (
            <Grid item>
              <MotionBox
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                <FeatureCard elevation={1}>
                  <motion.div variants={itemVariants}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        mb: 1.5, 
                        fontWeight: 700,
                        textAlign: "center",
                        color: theme.palette.primary.main,
                        letterSpacing: "-0.5px",
                      }}
                    >
                      HireSmart AI
                    </Typography>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mb: 5, 
                        textAlign: "center",
                        color: "text.secondary",
                        fontSize: "1.05rem",
                      }}
                    >
                      Supercharge your hiring with intelligent AI assistance
                    </Typography>
                  </motion.div>

                  {!isMediumScreen && (
                    <motion.div variants={itemVariants}>
                      <CentralIcon>
                        <SmartToyIcon sx={{ fontSize: 42 }} />
                      </CentralIcon>
                    </motion.div>
                  )}
                  
                  <motion.div variants={itemVariants}>
                    <FeatureItem>
                      <IconWrapper>
                        <SearchIcon sx={{ fontSize: 28 }} />
                      </IconWrapper>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} color={theme.palette.primary.main} gutterBottom>
                          Intelligent Job Posting
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create optimized job listings with AI suggestions that attract qualified candidates
                        </Typography>
                      </Box>
                    </FeatureItem>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <FeatureItem>
                      <IconWrapper>
                        <PersonIcon sx={{ fontSize: 28 }} />
                      </IconWrapper>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} color={theme.palette.primary.main} gutterBottom>
                          Smart Application Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Automatically rank and sort applications based on job requirements and skills
                        </Typography>
                      </Box>
                    </FeatureItem>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Box sx={{ mt: 4, mb: 2 }}>
                      <Divider>
                        <Chip 
                          label="Trusted by leading companies" 
                          size="small" 
                          sx={{ 
                            fontSize: "0.75rem", 
                            backgroundColor: `${theme.palette.primary.main}15`,
                            color: theme.palette.primary.main,
                          }} 
                        />
                      </Divider>
                    </Box>
                  </motion.div>
                </FeatureCard>
              </MotionBox>
            </Grid>
          )}
          
          <Grid item>
            <MotionBox
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <LoginCard elevation={1}>
                <motion.div variants={itemVariants}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 1, 
                      fontWeight: 600,
                      textAlign: "center",
                      color: theme.palette.primary.main,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    Welcome Back
                  </Typography>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 4, 
                      textAlign: "center",
                      color: "text.secondary",
                      fontSize: "0.95rem",
                    }}
                  >
                    Sign in to your HireSmart account
                  </Typography>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 3
                    }}
                  >
                    <Box>
                      <InputField
                        label="Email Address"
                        fullWidth
                        autoComplete="email"
                        value={formState.email.value}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        onKeyPress={handleKeyPress}
                        error={!!formState.email.error}
                        helperText={formState.email.error}
                        InputProps={{
                          sx: { fontSize: "0.95rem", py: 0.5 },
                          startAdornment: (
                            <InputAdornment position="start">
                              <AlternateEmailIcon sx={{ color: theme.palette.text.secondary }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    
                    <Box>
                      <InputField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        autoComplete="current-password"
                        value={formState.password.value}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        onKeyPress={handleKeyPress}
                        error={!!formState.password.error}
                        helperText={formState.password.error}
                        InputProps={{
                          sx: { fontSize: "0.95rem", py: 0.5 },
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: theme.palette.text.secondary }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    
                    <StyledButton
                      variant="contained"
                      fullWidth
                      onClick={handleLogin}
                      disabled={loading}
                      endIcon={!loading && <ArrowForwardIcon />}
                      sx={{ 
                        mt: 1.5,
                        bgcolor: theme.palette.primary.main,
                        "&:hover": { bgcolor: theme.palette.primary.dark },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" thickness={4} />
                      ) : (
                        "Sign In"
                      )}
                    </StyledButton>
                  </Box>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 3, 
                      textAlign: "center", 
                      color: "text.secondary",
                      fontSize: "0.9rem"
                    }}
                  >
                    Forgot your password? <Button 
                      sx={{ 
                        color: theme.palette.primary.main, 
                        fontWeight: 600, 
                        textTransform: 'none',
                        p: 0,
                        ml: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        }
                      }}>
                      Reset it here
                    </Button>
                  </Typography>
                </motion.div>
                
                {isMobile && (
                  <motion.div variants={itemVariants}>
                    <Box sx={{ mt: 5, pt: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        textAlign="center" 
                        color={theme.palette.primary.main} 
                        mb={2}
                      >
                        HireSmart AI Features
                      </Typography>
                      
                      <FeatureItem sx={{ mb: 2 }}>
                        <IconWrapper sx={{ width: 44, height: 44, borderRadius: "10px" }}>
                          <SearchIcon sx={{ fontSize: 24 }} />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body2" fontWeight={600} color={theme.palette.primary.main}>
                            Intelligent Job Posting
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                            AI-optimized job descriptions
                          </Typography>
                        </Box>
                      </FeatureItem>
                      
                      <FeatureItem>
                        <IconWrapper sx={{ width: 44, height: 44, borderRadius: "10px" }}>
                          <PersonIcon sx={{ fontSize: 24 }} />
                        </IconWrapper>
                        <Box>
                          <Typography variant="body2" fontWeight={600} color={theme.palette.primary.main}>
                            Smart Application Management
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                            Automated candidate ranking
                          </Typography>
                        </Box>
                      </FeatureItem>
                    </Box>
                  </motion.div>
                )}
              </LoginCard>
            </MotionBox>
          </Grid>
        </Grid>
      </Fade>
      
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: '1.2rem'
            }
          }}
        >
          Login successful! Redirecting...
        </Alert>
      </Snackbar>
    </LoginContainer>
  );
};

export default Login;